import type { CustomAxiosError, CustomAxiosInstance, CustomConfig } from '@/interfaces/IUseAxios'
import { backendUrl } from '@nami/utils'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import { useLogger } from '@/composable/useLogger'
import { ErrorCode } from '@/enum/ErrorCode'
import router from '@/router'
import { useGlobalStore } from '@/stores/globalStore'

const config: CustomConfig = {
    baseURL: backendUrl,
    withCredentials: true,
    noLoading: false,
    timeout: import.meta.env.MODE === 'staging' ? 60000 : 30000,
}

const checkRetryTimesAndSendLog = (error: CustomAxiosError) => {
    if (!error.response) {
        // handle the situation when error.response is undefined or null
        return
    }

    const { status, config } = error.response
    const retryCount = Number(error.config.__retryCount)
    const globalStore = useGlobalStore()

    if (status === 500) {
        if (retryCount >= 3) {
            useLogger().logError(
                `status code: ${status}, login name:${globalStore.appProps.loginName}, homeUrl: ${globalStore.appProps.homeUrl}, request config: ${JSON.stringify(
                    config,
                )}`,
            )
        }
    }
    else {
        useLogger().logInformation(
            `AxiosRetry times(${retryCount}):: status code: ${status}, request url: ${config.url}, homeUrl: ${globalStore.appProps.homeUrl}`,
        )
    }
}

const useAxios = axios.create(config) as CustomAxiosInstance

// @ts-expect-error: temp comment, will fix later
axiosRetry(useAxios, {
    retries: 3,
    retryDelay: retryCount => retryCount * 1000,
    retryCondition: (error: CustomAxiosError) => {
        error.config.__retryCount = (error.config.__retryCount ?? 0) + 1
        const shouldRetry = error.code === 'ECONNABORTED' || error.message === 'Network Error'

        if (error.config.method === 'post') {
            return false
        }

        checkRetryTimesAndSendLog(error)

        return shouldRetry
    },
})

const loadingControl = {
    on: (url: string) => {
        const globalStore = useGlobalStore()
        globalStore.addProcessApi(url)
    },
    off: (url: string) => {
        const globalStore = useGlobalStore()
        globalStore.deleteProcessApi(url)
    },
}

useAxios.interceptors.request.use(
    (config) => {
        if (!config.noLoading) {
            loadingControl.on(config.url ?? '')
        }
        return config
    },
    (error) => {
        loadingControl.off(error.config.url)
    },
)

useAxios.interceptors.response.use(
    (response) => {
        if (!response.config.noLoading) {
            loadingControl.off(response.config.url ?? '')
        }

        return response
    },
    (error) => {
        loadingControl.off(error.response?.config?.url ?? '')
        if (error.config?.skipInterceptor) {
            return Promise.reject(error)
        }

        const globalStore = useGlobalStore()
        let errorCode = error.response?.data?.errorCode

        if (error.response?.status === 401) {
            if (globalStore.appProps.homeUrl === 'close') {
                window.close()
            }
            else {
                location.href = globalStore.appProps.homeUrl
            }
            return Promise.reject(error)
        }

        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            const timeoutError = {
                message: 'API_REQUEST_TIMEOUT',
                requestUrl: error.config.url,
                requestMethod: error.config.method,
                timeout: error.config.timeout,
                loginName: globalStore.appProps.loginName,
                currentPath: window.location.pathname,
                userAgent: navigator.userAgent,
            }

            useLogger().logError('API_REQUEST_TIMEOUT', timeoutError)
        }

        if (!errorCode) {
            errorCode = ErrorCode.Unknown.toString()
        }

        if (error.response?.status && window.location.pathname !== '/payment/error') {
            const errorInfo = {
                message: error.message,
                code: error.code,
                request: {
                    url: error.config?.url,
                    method: error.config?.method,
                    headers: error.config?.headers,
                    data: error.config?.data,
                    timeout: error.config?.timeout,
                },
                response: {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    headers: error.response?.headers,
                    data: error.response?.data,
                },
                userInfo: {
                    loginName: globalStore.appProps.loginName,
                    currentPath: window.location.pathname,
                    userAgent: navigator.userAgent,
                },
                homeUrl: globalStore.appProps.homeUrl,
                timestamp: new Date().toISOString(),
            }
            useLogger().logInformation(JSON.stringify(errorInfo))
            router.push({ path: '/payment/error', query: { errorCode } }).then()
        }
        throw error
    },
)

export default useAxios
